"use client"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"

/**
 * Renders children into the `#page-bottom-slot` div that LayoutWrapper places
 * AFTER the site footer — the last thing on the page.
 *
 * Pages are server components and the footer lives in the client LayoutWrapper,
 * so there's no server-side way to compose past it. The server HTML (and the
 * first client render, so hydration matches) carries the children in place
 * inside a `hidden` wrapper: the text reaches crawlers that don't run JS, but
 * never shows next to the page body, which is where page-bottom fine print such
 * as photo credits must never appear. On mount the hidden copy is replaced by
 * the visible portal into the slot. Routes without the slot render nothing
 * after mount.
 */
export function PageBottomPortal({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null | undefined>(undefined)

  useEffect(() => {
    setSlot(document.getElementById("page-bottom-slot"))
  }, [])

  if (slot === undefined) return <div hidden>{children}</div>
  if (slot === null) return null
  return createPortal(children, slot)
}
